import { Result } from '@/types/api';
import { ResultStatus } from '@/types/api';
import {
  getAccessTokenFromStorage,
  setAccessTokenInStorage,
  setRefreshTokenInStorage,
} from '@/utils/auth';

const methods = ['get', 'post', 'patch', 'put', 'delete'];

type ApiOptions = RequestInit & {
  data?: unknown;
};

type ApiMethod = <T>(path: string, options?: ApiOptions) => Promise<Result<T>>;

export type Api = Record<'get' | 'post' | 'put' | 'patch' | 'delete', ApiMethod>;

const fetchFromApi: Api = methods.reduce(
  (accum, method) => ({
    ...accum,
    [method]: async <T>(path: string, options?: ApiOptions): Promise<Result<T>> => {
      const accessToken = getAccessTokenFromStorage();
      try {
        const response = await fetch(`${process.env.GATSBY_USER_API_URL}${path}`, {
          body: options?.data ? JSON.stringify(options.data) : undefined,
          ...options,
          mode: 'cors',
          method: method.toUpperCase(),
          headers: {
            'Content-Type': 'application/json',
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
            ...options?.headers,
          },
        });
        let result: any = {};
        try {
          result = await response.json();
        } catch (error) {
          console.error('Error parsing response', error);
        }
        const status = response.status;

        if (result?.accessToken) {
          setAccessTokenInStorage(result.accessToken);
          setRefreshTokenInStorage(result.refreshToken);
        }

        return status >= 200 && status < 300
          ? {
              status: ResultStatus.Success,
              data: result as T,
            }
          : {
              status: ResultStatus.Error,
              data: result,
              errors: result.errors,
            };
      } catch (error) {
        return {
          status: ResultStatus.Error,
          message: error as string,
        };
      }
    },
  }),
  {} as Api
);

export default fetchFromApi;
